
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { METRONOME_WORKER_CODE } from '../../services/metronomeWorkerCode';
import { getUnifiedAudioVideoGraph, disposeAudioGraph, AudioGraph } from '../../services/audioGraphService';
import { analyzeLatency } from '../../services/calibrationService';
import { registerSecureToken } from '../../services/memoryService';
import { api } from '../../services/api';
import { RecordingResult, TimeSignature } from '../../types';
import { normalizeHardwareLagMs } from '../../services/latencyUnits';

// CONSTANTE DE SYNCHRONISATION (Mesurée par l'utilisateur sur Mac)
const HARDWARE_OUTPUT_LATENCY_MS = 264.5; 

export type StudioStatus = 'BOOTING' | 'READY' | 'CALIBRATING' | 'CALIBRATED' | 'SILENT_CALIBRATION' | 'COUNT_IN' | 'RECORDING' | 'PROCESSING' | 'PREVIEW' | 'ERROR';

const getExtFromMime = (mime: string): string => {
    if (mime.includes('video/mp4')) return 'mp4';
    if (mime.includes('video/webm')) return 'webm';
    return 'webm';
};

export function useStudioManager(props: {
    mode: 'audio' | 'video' | 'screen',
    bpm: number,
    timeSignature: TimeSignature,
    calibrationOffset: number,
    orientation: 'landscape' | 'portrait',
    onCalibrationComplete: (offset: number) => void,
    onRecordingComplete: (res: RecordingResult) => void,
    autoStopAtBeat?: number,
    masterVideoRef?: React.RefObject<HTMLVideoElement | null>
}) {
    const [status, setStatus] = useState<StudioStatus>('BOOTING');
    const [error, setError] = useState<string | null>(null);
    const [errorType, setErrorType] = useState<string | null>(null);
    const [hardwareType, setHardwareType] = useState<'WIRED' | 'WIRELESS' | 'UNKNOWN'>('UNKNOWN');
    const [vuLevel, setVuLevel] = useState(0);
    const [displayBeat, setDisplayBeat] = useState(0);
    const [currentBarIdx, setCurrentBarIdx] = useState(0);
    const [reviewUrl, setReviewUrl] = useState<string | null>(null);
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedAudioId, setSelectedAudioId] = useState("default");
    const [selectedVideoId, setSelectedVideoId] = useState("default");
    const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
    const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
    const [calibratedLatency, setCalibratedLatency] = useState<number | null>(null);
    const [recordingMimeType, setRecordingMimeType] = useState<string>('video/webm');

    const graphRef = useRef<AudioGraph | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const metronomeWorkerRef = useRef<Worker | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const nextNoteTimeRef = useRef(0);
    const absoluteBeatRef = useRef(0);
    const stopTriggeredRef = useRef(false);
    const isMetronomeActiveRef = useRef(false);
    
    // START OFFSET & SLATE REFS
    const slateTimesCtx = useRef<number[]>([]);
    const recorderStartTimeCtx = useRef<number>(0);
    const recorderDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);

    const t0RecordingStart = useRef<number>(0);
    const t0RecorderStartCtx = useRef<number>(0);

    const statusRef = useRef<StudioStatus>('BOOTING');
    const bpmRef = useRef(props.bpm);
    const composerCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const videoElementRef = useRef<HTMLVideoElement | null>(null);
    const rafRef = useRef<number | null>(null);
    const renderRafRef = useRef<number | null>(null);
    const monitoringActiveRef = useRef<boolean>(false);

    useEffect(() => { statusRef.current = status; }, [status]);
    useEffect(() => { bpmRef.current = props.bpm; }, [props.bpm]);

    const setStatusSafe = (s: StudioStatus) => {
        statusRef.current = s;
        setStatus(s);
    };

    const renderCanvas = useCallback(() => {
        if (composerCanvasRef.current && videoElementRef.current && props.mode === 'video') {
            const ctx = composerCanvasRef.current.getContext('2d', { alpha: false });
            if (ctx) {
                ctx.drawImage(
                    videoElementRef.current, 
                    0, 0, 
                    composerCanvasRef.current.width, 
                    composerCanvasRef.current.height
                );
            }
        }
        renderRafRef.current = requestAnimationFrame(renderCanvas);
    }, [props.mode]);

    const monitorLevels = useCallback(() => {
        if (!monitoringActiveRef.current) return;
        const graph = graphRef.current;
        if (graph && graph.isReady && graph.meterAnalyser) {
            const arr = new Float32Array(graph.meterAnalyser.fftSize);
            graph.meterAnalyser.getFloatTimeDomainData(arr);
            let sum = 0;
            for(let i = 0; i < arr.length; i++) sum += arr[i] * arr[i];
            const rms = Math.sqrt(sum / arr.length);
            setVuLevel(Math.min(1, rms * 40)); 
        }
        rafRef.current = requestAnimationFrame(monitorLevels);
    }, []);

    const playClick = (time: number, isAccented: boolean, beatIdx: number) => {
        const graph = graphRef.current;
        if (!graph || !isMetronomeActiveRef.current) return;
        const ctx = graph.ctx;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(isAccented ? 1200 : 800, time);
        
        // Clic pour l'utilisateur
        g.gain.setValueAtTime(0, time);
        g.gain.linearRampToValueAtTime(0.4, time + 0.002);
        g.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
        osc.connect(g); 
        g.connect(ctx.destination);
        
        // INJECTION DIRECTE DANS LE RECORDER (SLATE)
        if (recorderDestRef.current && beatIdx < 4) {
            const sg = ctx.createGain();
            sg.gain.setValueAtTime(0, time);
            sg.gain.linearRampToValueAtTime(0.12, time + 0.002); 
            sg.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
            osc.connect(sg);
            sg.connect(recorderDestRef.current);
            // On mémorise l'heure du clic
            slateTimesCtx.current.push(time);
        }

        osc.start(time); osc.stop(time + 0.06);
    };

    const scheduler = () => {
        if (!isMetronomeActiveRef.current || !graphRef.current) return;
        
        const graph = graphRef.current;
        const ctx = graph.ctx;
        const lookahead = 0.1;

        while (nextNoteTimeRef.current < ctx.currentTime + lookahead) {
            const bpm = bpmRef.current;
            const secPerBeat = 60 / bpm;
            const beatsPerBar = parseInt(props.timeSignature.split('/')[0]) || 4;
            const time = nextNoteTimeRef.current;
            const currentBeatIdx = absoluteBeatRef.current;
            
            let isAccented = false;
            if (currentBeatIdx < 4) {
                isAccented = currentBeatIdx === 0;
                
                // Visual Sync
                const delayMs = Math.max(0, (time - ctx.currentTime) * 1000);
                setTimeout(() => {
                    if (statusRef.current === 'COUNT_IN') {
                        setDisplayBeat(currentBeatIdx + 1);
                    }
                }, delayMs);

                // NOUVEAU: On lance la vidéo prof dès le PREMIER clic du décompte élève (beat 0)
                // pour synchroniser les deux décomptes.
                if (currentBeatIdx === 0) {
                    setTimeout(() => {
                        if (props.masterVideoRef?.current) {
                            props.masterVideoRef.current.currentTime = 0;
                            props.masterVideoRef.current.play().catch(console.error);
                        }
                    }, delayMs);
                }
                
                if (currentBeatIdx === 3) {
                    t0RecordingStart.current = time + secPerBeat;
                    setTimeout(() => { 
                        setStatusSafe('RECORDING'); 
                    }, (time - ctx.currentTime + secPerBeat) * 1000);
                }
            } else {
                const recIdx = currentBeatIdx - 4;
                const beatInBar = (recIdx % beatsPerBar) + 1;
                isAccented = beatInBar === 1;
                
                const delayMs = Math.max(0, (time - ctx.currentTime) * 1000);
                setTimeout(() => {
                    if (statusRef.current === 'RECORDING') {
                        setDisplayBeat(beatInBar);
                        setCurrentBarIdx(Math.floor(recIdx / beatsPerBar));
                    }
                }, delayMs);

                if (props.autoStopAtBeat !== undefined && recIdx >= props.autoStopAtBeat - 1 && !stopTriggeredRef.current) {
                    stopTriggeredRef.current = true;
                    setTimeout(() => stopRecording(), (time - ctx.currentTime + secPerBeat) * 1000);
                }
            }

            playClick(time, isAccented, currentBeatIdx);
            nextNoteTimeRef.current += secPerBeat; 
            absoluteBeatRef.current++;
        }
    };

    const startRecording = async () => {
        if (!graphRef.current) return;
        const graph = graphRef.current;
        const ctx = graph.ctx;
        await ctx.resume();
        
        // RESET
        isMetronomeActiveRef.current = true;
        absoluteBeatRef.current = 0;
        setDisplayBeat(1); 
        stopTriggeredRef.current = false;
        slateTimesCtx.current = [];
        chunksRef.current = [];

        const recorderDest = ctx.createMediaStreamDestination();
        recorderDestRef.current = recorderDest;
        graph.source.connect(recorderDest);

        // Timing initial
        nextNoteTimeRef.current = ctx.currentTime + 0.1 + (HARDWARE_OUTPUT_LATENCY_MS / 1000);
        
        let streamToRecord: MediaStream;
        if (props.mode === 'video' && composerCanvasRef.current) {
            const videoStream = composerCanvasRef.current.captureStream(30);
            streamToRecord = new MediaStream([...videoStream.getVideoTracks(), ...recorderDest.stream.getAudioTracks()]);
        } else {
            streamToRecord = recorderDest.stream;
        }

        const selectedType = [
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/webm',
            'video/mp4'
        ].find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm';
        
        setRecordingMimeType(selectedType);

        const recorder = new MediaRecorder(streamToRecord, { mimeType: selectedType });
        recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
        recorder.onstart = () => { 
            recorderStartTimeCtx.current = ctx.currentTime; 
            t0RecorderStartCtx.current = ctx.currentTime; 
        };
        
        recorder.start(100); 
        mediaRecorderRef.current = recorder;

        setStatusSafe('COUNT_IN');
        metronomeWorkerRef.current?.postMessage("start");
    };

    const stopRecording = useCallback(async () => {
        isMetronomeActiveRef.current = false;
        if (statusRef.current !== 'RECORDING' && statusRef.current !== 'COUNT_IN') return;
        setStatusSafe('PROCESSING');
        metronomeWorkerRef.current?.postMessage("stop");
        if (props.masterVideoRef?.current) props.masterVideoRef.current.pause();
        
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: recordingMimeType });
                const url = URL.createObjectURL(blob);
                setReviewUrl(url);
                setStatusSafe('PREVIEW');
            };
            mediaRecorderRef.current.stop();
        }
    }, [recordingMimeType, props.masterVideoRef]);

    const validateTake = async () => {
        if (chunksRef.current.length === 0) return;
        setStatusSafe('PROCESSING');
        const blob = new Blob(chunksRef.current, { type: recordingMimeType });
        const ext = getExtFromMime(recordingMimeType);
        const file = new File([blob], `take_${Date.now()}.${ext}`, { type: recordingMimeType });
        
        const driftSeconds = Math.max(0, t0RecorderStartCtx.current - t0RecordingStart.current);
        const finalLag = props.calibrationOffset + (driftSeconds * 1000); 

        const metadata = { 
            teacherStartTime: 0, 
            recordedAtBpm: props.bpm, 
            calibrationOffset: normalizeHardwareLagMs(finalLag), 
            source: "Live", 
            timeSignature: props.timeSignature,
            slateData: {
                expectedTimes: slateTimesCtx.current.map(t => t - recorderStartTimeCtx.current),
                recorderStartTime: recorderStartTimeCtx.current
            }
        };

        try {
            const token = await api.getSessionToken();
            props.onRecordingComplete({ file, metadata: { ...metadata, captureToken: token } });
        } catch (e) {
            props.onRecordingComplete({ file, metadata });
        }
    };

    const startCalibration = async () => {
        if (statusRef.current === 'RECORDING' || statusRef.current === 'CALIBRATING' || !graphRef.current) return;
        try {
            const graph = graphRef.current; const ctx = graph.ctx; await ctx.resume();
            setStatusSafe('CALIBRATING'); setCalibratedLatency(null); setDisplayBeat(1);
            const track = graph.audioStream.getAudioTracks()[0];
            const constraints = track.getConstraints();
            
            try { await track.applyConstraints({ echoCancellation: false, noiseSuppression: false, autoGainControl: false }); } catch (e) {}
            
            const calibChunks: Blob[] = [];
            let recorder = new MediaRecorder(graph.audioStream);
            recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) calibChunks.push(e.data); };
            const expectedOffsets: number[] = [];
            
            recorder.onstop = async () => {
                try {
                    const blob = new Blob(calibChunks, { type: recorder.mimeType });
                    const ab = await blob.arrayBuffer();
                    const decoded = await ctx.decodeAudioData(ab);
                    const res = analyzeLatency(decoded.getChannelData(0), expectedOffsets, decoded.sampleRate);
                    if (res.success && res.latencySec !== undefined) {
                        const ms = normalizeHardwareLagMs(res.latencySec * 1000);
                        setCalibratedLatency(ms); setHardwareType(res.hardwareType || 'UNKNOWN');
                        props.onCalibrationComplete(ms); setStatusSafe('CALIBRATED');
                        setTimeout(() => setStatusSafe('READY'), 2000);
                    } else {
                        throw new Error("Signal instable.");
                    }
                } catch (e: any) { setError(e.message); setStatusSafe('ERROR'); }
                finally { try { await track.applyConstraints(constraints); } catch (e) {} }
            };
            
            recorder.onstart = () => {
                const now = ctx.currentTime; const startRel = 0.6;
                for (let i = 0; i < 4; i++) {
                    const tBuffer = startRel + (i * 1.2); 
                    const tCtx = now + tBuffer; expectedOffsets.push(tBuffer);
                    const osc = ctx.createOscillator(); const g = ctx.createGain();
                    osc.type = 'sine'; osc.frequency.setValueAtTime(1000, tCtx);
                    g.gain.setValueAtTime(0, tCtx); g.gain.linearRampToValueAtTime(1.0, tCtx + 0.005); g.gain.exponentialRampToValueAtTime(0.001, tCtx + 0.1);
                    osc.connect(g); g.connect(ctx.destination);
                    osc.start(tCtx); osc.stop(tCtx + 0.12);
                    setTimeout(() => setDisplayBeat(i + 1), tBuffer * 1000);
                }
                setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, 6000);
            };
            recorder.start();
        } catch (e: any) { setError(e.message); setStatusSafe('ERROR'); }
    };

    const init = useCallback(async () => {
        setStatusSafe('BOOTING');
        try {
            await disposeAudioGraph();
            const devs = await navigator.mediaDevices.enumerateDevices();
            setAudioDevices(devs.filter(d => d.kind === 'audioinput'));
            setVideoDevices(devs.filter(d => d.kind === 'videoinput'));
            const graph = await getUnifiedAudioVideoGraph('ANALYSIS', selectedAudioId, selectedVideoId, props.mode === 'video');
            graphRef.current = graph; setAnalyser(graph.meterAnalyser);
            
            if (graph.videoStream) {
                setPreviewStream(graph.videoStream);
                if (!composerCanvasRef.current) composerCanvasRef.current = document.createElement('canvas'); 
                composerCanvasRef.current.width = 1280; 
                composerCanvasRef.current.height = 720;
                
                if (!videoElementRef.current) { 
                    videoElementRef.current = document.createElement('video'); 
                    videoElementRef.current.muted = true; 
                    videoElementRef.current.playsInline = true;
                }
                videoElementRef.current.srcObject = graph.videoStream; 
                videoElementRef.current.play().catch(() => {});
                
                if (renderRafRef.current) cancelAnimationFrame(renderRafRef.current);
                renderCanvas();
            }

            if (metronomeWorkerRef.current) metronomeWorkerRef.current.terminate();
            const blob = new Blob([METRONOME_WORKER_CODE], { type: 'application/javascript' });
            metronomeWorkerRef.current = new Worker(URL.createObjectURL(blob), { type: 'module' });
            metronomeWorkerRef.current.onmessage = (e) => { if (e.data.type === "tick") scheduler(); };
            
            monitoringActiveRef.current = true;
            monitorLevels();

            setStatusSafe('READY');
        } catch (err: any) { setError(err.message); setStatusSafe('ERROR'); }
    }, [selectedAudioId, selectedVideoId, props.mode, monitorLevels, renderCanvas]);

    useEffect(() => { 
        init(); 
        return () => { 
            isMetronomeActiveRef.current = false;
            monitoringActiveRef.current = false;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (renderRafRef.current) cancelAnimationFrame(renderRafRef.current);
            disposeAudioGraph(); 
        }; 
    }, [init]);

    return { 
        status, error, errorType, hardwareType, vuLevel, displayBeat, currentBarIdx, reviewUrl, audioDevices, videoDevices,
        selectedAudioId, selectedVideoId, setSelectedAudioId, setSelectedVideoId, previewStream, analyser, calibratedLatency, 
        startCalibration, startRecording, stopRecording, validateTake, retry: init, reboot: init,
        recordingMimeType, errorTypeOriginal: errorType
    };
}
